import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import scheduleService from '../../../../services/community/scheduleService';
import { CreateShiftBody, ScheduleCommunityModel, ScheduleModel } from '../../../../models/schedule.model';
import { Frequency } from '../../../../types/event';
import useIsMainBusinessCommunity from '../../../../hooks/business/useIsMainBusinessCommunity';
import Button from '../../../components/buttons/Button';
import Input from '../../../components/misc/Input';
import DatePicker from '../../../components/forms/DatePicker';
import DropdownSelect from '../../../components/forms/DropdownSelect';
import Slider from '../../../components/misc/Slider';
import StepFlow, { StepFlowStep } from '../../../components/misc/StepFlow';
import ShiftCommunityAssignmentsEditor, {
  DraftShiftCommunityAssignment,
  getCommunityAssignmentsPayload,
  getTotalCommunityRequiredCount,
} from './ShiftCommunityAssignmentsEditor';
import ShiftMemberAssignmentsEditor, {
  DraftShiftMemberAssignee,
  getMemberAssignmentsPayload,
} from './ShiftMemberAssignmentsEditor';
import {
  getDefaultRecurrenceEndDate,
  getRecurrenceSummary,
  parseShiftCapacity,
} from './shiftFormUtils';

export type ShiftAssignmentMode = 'members' | 'communities';

export interface CreateShiftFormState {
  shiftName: string;
  shiftDescription: string;
  shiftLocation: string;
  shiftStartTime?: Date;
  shiftEndTime?: Date;
  shiftAssigneeCount: string;
  shiftAllowSelfJoin: boolean;
  shiftAllowShiftExchangeRequests: boolean;
  assignmentMode: ShiftAssignmentMode;
  selectedAssignees: DraftShiftMemberAssignee[];
  shiftCommunityAssignments: DraftShiftCommunityAssignment[];
  isRecurringShift: boolean;
  shiftFrequency?: Frequency;
  shiftRecurrenceEndDate?: Date;
}

const initialFormState: CreateShiftFormState = {
  shiftName: '',
  shiftDescription: '',
  shiftLocation: '',
  shiftStartTime: undefined,
  shiftEndTime: undefined,
  shiftAssigneeCount: '',
  shiftAllowSelfJoin: false,
  shiftAllowShiftExchangeRequests: false,
  assignmentMode: 'members',
  selectedAssignees: [],
  shiftCommunityAssignments: [],
  isRecurringShift: false,
  shiftFrequency: undefined,
  shiftRecurrenceEndDate: undefined,
};

interface StepNavigationProps {
  setStep: (diff: 1 | -1) => void;
}

interface DetailsStepProps extends StepNavigationProps {
  form: CreateShiftFormState;
  setForm: React.Dispatch<React.SetStateAction<CreateShiftFormState>>;
  scheduleName?: string;
}

interface AssignmentStepProps extends StepNavigationProps {
  form: CreateShiftFormState;
  setForm: React.Dispatch<React.SetStateAction<CreateShiftFormState>>;
}

interface ScheduleStepProps extends StepNavigationProps {
  form: CreateShiftFormState;
  setForm: React.Dispatch<React.SetStateAction<CreateShiftFormState>>;
  isMainBusinessCommunity: boolean;
  linkedScheduleCommunities: ScheduleCommunityModel[];
  onSubmit: () => void;
  saving: boolean;
}

function DetailsStep({ form, setForm, setStep, scheduleName }: DetailsStepProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const invalidShiftEndTime =
    !!form.shiftStartTime &&
    !!form.shiftEndTime &&
    new Date(form.shiftEndTime) <= new Date(form.shiftStartTime);

  const recurrenceSummary = getRecurrenceSummary({
    frequency: form.shiftFrequency,
    startTime: form.shiftStartTime,
    language: i18n.language,
    t,
  });

  const toggleRecurringShift = (value: boolean) => {
    setForm((prev) => {
      if (!value) {
        return {
          ...prev,
          isRecurringShift: false,
          shiftFrequency: undefined,
          shiftRecurrenceEndDate: undefined,
        };
      }

      const nextFrequency = prev.shiftFrequency ?? Frequency.WEEKLY;
      return {
        ...prev,
        isRecurringShift: true,
        shiftFrequency: nextFrequency,
        shiftRecurrenceEndDate: getDefaultRecurrenceEndDate(nextFrequency, prev.shiftStartTime),
      };
    });
  };

  const handleShiftFrequencyChange = (value: Frequency | undefined) => {
    setForm((prev) => ({
      ...prev,
      shiftFrequency: value,
      shiftRecurrenceEndDate: value
        ? getDefaultRecurrenceEndDate(value, prev.shiftStartTime)
        : undefined,
    }));
  };

  const hasValidRecurrence =
    !form.isRecurringShift || (!!form.shiftRecurrenceEndDate && !!form.shiftFrequency);

  return (
    <StepFlowStep
      title={t('page.event.schedules.createShiftFlow.steps.details.title')}
      subtitle={
        scheduleName
          ? t('page.event.schedules.createShiftFlow.steps.details.subtitle', { scheduleName })
          : undefined
      }
      setStep={setStep}
      showPrev={false}
      disabled={
        !form.shiftName.trim() ||
        !form.shiftStartTime ||
        invalidShiftEndTime ||
        !hasValidRecurrence
      }>
      <Input
        id="create-shift-name"
        label={t('page.event.schedules.fields.shiftName')}
        type="text"
        value={form.shiftName}
        onChange={(event) => setForm((prev) => ({ ...prev, shiftName: event.target.value }))}
        required
      />
      <Input
        id="create-shift-description"
        label={t('page.event.schedules.fields.shiftDescription')}
        type="textarea"
        value={form.shiftDescription}
        onChange={(event) => setForm((prev) => ({ ...prev, shiftDescription: event.target.value }))}
      />
      <Input
        id="create-shift-location"
        label={t('page.event.schedules.fields.shiftLocation')}
        type="text"
        value={form.shiftLocation}
        onChange={(event) => setForm((prev) => ({ ...prev, shiftLocation: event.target.value }))}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-primary-700">
            {t('page.event.schedules.fields.startDate')} *
          </label>
          <DatePicker
            date={form.shiftStartTime}
            setDate={(date) => setForm((prev) => ({ ...prev, shiftStartTime: date }))}
            includeTime
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-primary-700">
            {t('page.event.schedules.fields.endDate')}
          </label>
          <DatePicker
            date={form.shiftEndTime}
            setDate={(date) => setForm((prev) => ({ ...prev, shiftEndTime: date }))}
            includeTime
          />
        </div>
      </div>
      {invalidShiftEndTime && (
        <p className="text-sm text-danger">{t('page.event.schedules.createShiftFlow.invalidEndTime')}</p>
      )}

      <div className="border-t border-secondary-200 pt-4">
        <Slider
          label={t('page.event.schedules.recurrence.label')}
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={form.isRecurringShift}
          onChange={toggleRecurringShift}
        />

        {form.isRecurringShift && (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-primary-700">
                {t('page.event.schedules.recurrence.frequency')}
              </label>
              <DropdownSelect
                options={Object.values(Frequency).map((frequency) => ({
                  label: t(`component.eventDatesForm.frequency.${frequency.toLowerCase()}`),
                  value: frequency,
                }))}
                value={form.shiftFrequency}
                setValue={handleShiftFrequencyChange}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-primary-700">
                {t('page.event.schedules.recurrence.endDate')}
              </label>
              <DatePicker
                includeTime={false}
                date={form.shiftRecurrenceEndDate}
                setDate={(date) => setForm((prev) => ({ ...prev, shiftRecurrenceEndDate: date }))}
              />
            </div>

            <p className="rounded-xl bg-primary-100 px-3 py-2 text-sm text-primary-800">
              {t('page.event.schedules.recurrence.summaryLabel')}: {recurrenceSummary}
            </p>
          </div>
        )}
      </div>
    </StepFlowStep>
  );
}

function AssignmentStep({ form, setForm, setStep }: AssignmentStepProps): JSX.Element {
  const { t } = useTranslation();

  const parsedShiftCapacity = parseShiftCapacity(form.shiftAssigneeCount);
  const hasValidShiftAssigneeCount = parsedShiftCapacity !== undefined;

  return (
    <StepFlowStep
      title={t('page.event.schedules.createShiftFlow.steps.assignment.title')}
      subtitle={t('page.event.schedules.createShiftFlow.steps.assignment.subtitle')}
      setStep={setStep}
      disabled={!hasValidShiftAssigneeCount}>
      <Input
        id="create-shift-assignee-count"
        label={t('page.event.schedules.fields.assigneeCount')}
        type="number"
        value={form.shiftAssigneeCount}
        onChange={(event) => setForm((prev) => ({ ...prev, shiftAssigneeCount: event.target.value }))}
        min={1}
        required
      />
      <Slider
        label={t('page.event.schedules.fields.allowSelfJoin')}
        items={[
          { label: t('general.no'), value: false },
          { label: t('general.yes'), value: true },
        ]}
        value={form.shiftAllowSelfJoin}
        onChange={(value) => setForm((prev) => ({ ...prev, shiftAllowSelfJoin: value }))}
      />
      <Slider
        label={t('page.event.schedules.fields.allowShiftExchangeRequests')}
        items={[
          { label: t('general.no'), value: false },
          { label: t('general.yes'), value: true },
        ]}
        value={form.shiftAllowShiftExchangeRequests}
        onChange={(value) => setForm((prev) => ({ ...prev, shiftAllowShiftExchangeRequests: value }))}
      />
    </StepFlowStep>
  );
}

function ScheduleStep({
  form,
  setForm,
  setStep,
  isMainBusinessCommunity,
  linkedScheduleCommunities,
  onSubmit,
  saving,
}: ScheduleStepProps): JSX.Element {
  const { t } = useTranslation();

  const parsedShiftCapacity = parseShiftCapacity(form.shiftAssigneeCount);
  const useCommunitiesMode = isMainBusinessCommunity && form.assignmentMode === 'communities';
  const useMembersMode = !isMainBusinessCommunity || form.assignmentMode === 'members';

  const hasCommunityAssignmentMismatch =
    useCommunitiesMode &&
    form.shiftCommunityAssignments.length > 0 &&
    parsedShiftCapacity !== undefined &&
    getTotalCommunityRequiredCount(form.shiftCommunityAssignments) !== parsedShiftCapacity;

  const hasTooManyMemberAssignees =
    useMembersMode &&
    parsedShiftCapacity !== undefined &&
    form.selectedAssignees.length > parsedShiftCapacity;

  const handleAssignmentModeChange = (mode: ShiftAssignmentMode) => {
    setForm((prev) => ({
      ...prev,
      assignmentMode: mode,
      selectedAssignees: mode === 'members' ? prev.selectedAssignees : [],
      shiftCommunityAssignments: mode === 'communities' ? prev.shiftCommunityAssignments : [],
    }));
  };

  const canSubmit =
    !!parsedShiftCapacity && !hasCommunityAssignmentMismatch && !hasTooManyMemberAssignees;

  return (
    <StepFlowStep
      title={t('page.event.schedules.createShiftFlow.steps.schedule.title')}
      subtitle={t('page.event.schedules.createShiftFlow.steps.schedule.subtitle')}
      setStep={setStep}
      showNext={false}
      onSubmit={onSubmit}
      submitText={t('page.event.schedules.createShift')}
      disabled={!canSubmit || saving}>
      <p className="rounded-xl bg-primary-100 px-3 py-2 text-sm text-primary-800">
        {t('page.event.schedules.createShiftFlow.fastAssignHint', {
          feature: t('page.event.schedules.fastAssigning.tab'),
        })}
      </p>

      {isMainBusinessCommunity && (
        <Slider
          label={t('page.event.schedules.fastAssigning.mode')}
          items={[
            {
              label: t('page.event.schedules.fastAssigning.modes.members'),
              value: 'members' as ShiftAssignmentMode,
            },
            {
              label: t('page.event.schedules.fastAssigning.modes.communities'),
              value: 'communities' as ShiftAssignmentMode,
            },
          ]}
          value={form.assignmentMode}
          onChange={handleAssignmentModeChange}
        />
      )}

      {useMembersMode && (
        <ShiftMemberAssignmentsEditor
          assignees={form.selectedAssignees}
          onChange={(selectedAssignees) => setForm((prev) => ({ ...prev, selectedAssignees }))}
          capacity={parsedShiftCapacity}
        />
      )}

      {useCommunitiesMode && (
        <ShiftCommunityAssignmentsEditor
          linkedCommunities={linkedScheduleCommunities}
          assignments={form.shiftCommunityAssignments}
          onChange={(shiftCommunityAssignments) =>
            setForm((prev) => ({ ...prev, shiftCommunityAssignments }))
          }
        />
      )}

      {hasCommunityAssignmentMismatch && (
        <p className="text-sm text-danger">{t('page.event.schedules.communities.quotaMismatch')}</p>
      )}

      {hasTooManyMemberAssignees && (
        <p className="text-sm text-danger">{t('page.event.schedules.capacityReached')}</p>
      )}
    </StepFlowStep>
  );
}

export default function CreateShiftPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const isMainBusinessCommunity = useIsMainBusinessCommunity();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<CreateShiftFormState>(initialFormState);
  const [schedule, setSchedule] = useState<ScheduleModel | null>(null);
  const [linkedScheduleCommunities, setLinkedScheduleCommunities] = useState<ScheduleCommunityModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  useEffect(() => {
    const loadSchedule = async () => {
      if (!scheduleId) {
        navigate('/events', { state: { tab: 'schedules' } });
        return;
      }

      try {
        setLoading(true);
        const schedules = await scheduleService.listSchedules();
        const found = schedules.find((entry) => entry.scheduleId === Number(scheduleId));

        if (!found) {
          toast.error(t('general.error'));
          navigate('/events', { state: { tab: 'schedules' } });
          return;
        }

        setSchedule(found);

        if (isMainBusinessCommunity) {
          const communities = await scheduleService.listScheduleCommunities(found.scheduleId);
          setLinkedScheduleCommunities(communities);
        }
      } catch {
        toast.error(t('general.error'));
        navigate('/events', { state: { tab: 'schedules' } });
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [isMainBusinessCommunity, navigate, scheduleId, t]);

  const handleCreateShift = async () => {
    if (!schedule || !form.shiftName.trim() || !form.shiftStartTime) {
      return;
    }

    const parsedShiftCapacity = parseShiftCapacity(form.shiftAssigneeCount);
    if (!parsedShiftCapacity) {
      return;
    }

    const useCommunitiesMode = isMainBusinessCommunity && form.assignmentMode === 'communities';
    const useMembersMode = !isMainBusinessCommunity || form.assignmentMode === 'members';

    const hasCommunityAssignmentMismatch =
      useCommunitiesMode &&
      form.shiftCommunityAssignments.length > 0 &&
      getTotalCommunityRequiredCount(form.shiftCommunityAssignments) !== parsedShiftCapacity;

    if (hasCommunityAssignmentMismatch) {
      toast.error(t('page.event.schedules.communities.quotaMismatch'));
      return;
    }

    if (useMembersMode && form.selectedAssignees.length > parsedShiftCapacity) {
      toast.error(t('page.event.schedules.capacityReached'));
      return;
    }

    if (form.isRecurringShift && (!form.shiftRecurrenceEndDate || !form.shiftFrequency)) {
      return;
    }

    const invalidShiftEndTime =
      !!form.shiftEndTime && new Date(form.shiftEndTime) <= new Date(form.shiftStartTime);

    if (invalidShiftEndTime) {
      toast.error(t('page.event.schedules.createShiftFlow.invalidEndTime'));
      return;
    }

    try {
      setSaving(true);

      const communityAssignments =
        useCommunitiesMode && form.shiftCommunityAssignments.length > 0
          ? getCommunityAssignmentsPayload(form.shiftCommunityAssignments)
          : undefined;

      const memberAssignments = useMembersMode
        ? getMemberAssignmentsPayload(form.selectedAssignees)
        : { participantsIds: [], externalInvites: [] };

      const body: CreateShiftBody = {
        name: form.shiftName.trim(),
        description: form.shiftDescription.trim() || undefined,
        location: form.shiftLocation.trim() || undefined,
        startTime: form.shiftStartTime.toISOString(),
        endTime: form.shiftEndTime ? form.shiftEndTime.toISOString() : undefined,
        assigneeCount: parsedShiftCapacity,
        allowSelfJoin: form.shiftAllowSelfJoin,
        allowShiftExchangeRequests: form.shiftAllowShiftExchangeRequests,
        communityAssignments,
        participantsIds:
          memberAssignments.participantsIds.length > 0
            ? memberAssignments.participantsIds
            : undefined,
        externalInvites:
          memberAssignments.externalInvites.length > 0
            ? memberAssignments.externalInvites
            : undefined,
        recurrence:
          form.isRecurringShift && form.shiftRecurrenceEndDate && form.shiftFrequency
            ? {
                frequency: form.shiftFrequency,
                recurrenceEndDate: form.shiftRecurrenceEndDate.toISOString(),
              }
            : undefined,
      };

      await scheduleService.createShift(schedule.scheduleId, body);
      toast.success(t('page.event.schedules.messages.shift.created'));
      navigate(`/events/schedule/${schedule.scheduleId}`);
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    <DetailsStep
      key="details"
      form={form}
      setForm={setForm}
      setStep={handleStep}
      scheduleName={schedule?.name}
    />,
    <AssignmentStep key="assignment" form={form} setForm={setForm} setStep={handleStep} />,
    <ScheduleStep
      key="schedule"
      form={form}
      setForm={setForm}
      setStep={handleStep}
      isMainBusinessCommunity={isMainBusinessCommunity}
      linkedScheduleCommunities={linkedScheduleCommunities}
      onSubmit={handleCreateShift}
      saving={saving}
    />,
  ];

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-primary-500">
        {t('general.loading')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="tertiary"
          onClick={() => navigate(schedule ? `/events/schedule/${schedule.scheduleId}` : '/events')}>
          <FaArrowLeft className="size-5" />
          <span>{t('general.back')}</span>
        </Button>
        <h1 className="text-2xl font-semibold text-primary-900">
          {t('page.event.schedules.createShift')}
        </h1>
      </div>

      <StepFlow indexState={[step, setStep]} steps={steps} className="bg-secondary-100" />
    </div>
  );
}
